import React, { useState, useEffect } from 'react';
import useLoyaltyStore from './useLoyaltyStore';
import AnimatedModal from '../shared/AnimatedModal';

const CategoryFormModal = () => {
  const { closeModal, modalMode, editingItem, saveCategory } = useLoyaltyStore();
  const isEdit = modalMode === 'edit';

  const [form, setForm] = useState({
    name: '', slug: '', icon: '', description: '', position: '1', show_in_app: true
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEdit && editingItem) {
      setForm({
        name: editingItem.name || '',
        slug: editingItem.slug || '',
        icon: editingItem.icon || '',
        description: editingItem.description || '',
        position: editingItem.position ?? '1',
        show_in_app: editingItem.show_in_app !== false,
      });
    }
  }, [isEdit, editingItem]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    setForm(prev => {
      const updated = { ...prev, [name]: newValue };
      if (name === 'name' && !isEdit) {
        updated.slug = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      }
      return updated;
    });
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Category name is required';
    if (!form.description.trim()) errs.description = 'Description is required';
    return errs;
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setSaving(true);
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    const fd = new FormData();
    fd.append('authenticity_token', csrfToken);
    fd.append('category[name]', form.name);
    fd.append('category[icon]', form.icon);
    fd.append('category[description]', form.description);
    fd.append('category[position]', form.position);
    fd.append('category[show_in_app]', form.show_in_app);

    try { await saveCategory(fd); } finally { setSaving(false); }
  };

  const footer = (handleClose) => (
    <>
      <button type="button" className="btn btn-outline-secondary" onClick={handleClose}>Cancel</button>
      <button type="button" className="btn btn-success" disabled={saving} onClick={handleSubmit}>
        {saving ? 'Saving...' : 'Save Category'}
      </button>
    </>
  );

  return (
    <AnimatedModal title={isEdit ? 'Edit Category' : 'Add New Category'} onClose={closeModal} footer={footer}>
      <div className="form-group">
        <label>Category Name <span className="text-danger">*</span></label>
        <input type="text" className={`form-control${errors.name ? ' is-invalid' : ''}`} name="name" value={form.name} onChange={handleChange} placeholder="Enter category name" />
        <i className="form-group__bar" />
        {errors.name && <div className="invalid-feedback">{errors.name}</div>}
      </div>

      <div className="form-group">
        <label>Slug</label>
        <input type="text" className="form-control" name="slug" value={form.slug} onChange={handleChange} placeholder="auto-generated-from-name" disabled={isEdit} />
        <i className="form-group__bar" />
        <small className="text-muted">URL-friendly version of the name</small>
      </div>

      <div className="form-group">
        <label>Icon Picker</label>
        <input type="text" className="form-control" name="icon" value={form.icon} onChange={handleChange} placeholder="Enter an emoji or icon" />
        <i className="form-group__bar" />
        <small className="text-muted">Enter an emoji or icon</small>
      </div>

      <div className="form-group">
        <label>Description <span className="text-danger">*</span></label>
        <textarea className={`form-control${errors.description ? ' is-invalid' : ''}`} name="description" value={form.description} onChange={handleChange} rows={3} placeholder="Enter description" />
        <i className="form-group__bar" />
        {errors.description && <div className="invalid-feedback">{errors.description}</div>}
      </div>

      <div className="form-group">
        <label>Sort Order</label>
        <input type="number" className="form-control" name="position" value={form.position} onChange={handleChange} min="0" />
        <i className="form-group__bar" />
      </div>

      <div className="d-flex align-items-center justify-content-between">
        <label className="mb-0">Show in Mobile App</label>
        <div className="toggle-switch" style={{ marginBottom: 0 }}>
          <input type="checkbox" className="toggle-switch__checkbox" id="show-in-app" name="show_in_app" checked={form.show_in_app} onChange={handleChange} />
          <i className="toggle-switch__helper" />
        </div>
      </div>
    </AnimatedModal>
  );
};

export default CategoryFormModal;
